import * as THREE from "three"
import DataFetcher from '../Data/arenafetcher';
import SpriteElement from '../World/SpriteElement';
import LineElement from '../World/LineElement';
import { existingChannelIDs, linesToBillboardEls, billboardEls, upcomingConnections } from '../World/sets';
import Experience from "../Experience";
import Plane from "../World/Plane";
import FetchWrapper from "../Data/fetchwrapper";
import {
    getConnectionsHref,
    normalizeBlockBaseType,
    normalizeBlockType,
    normalizeConnections
} from "../Data/arenaNormalizer";
import type { ArenaBlock, ArenaConnectionSummary, ArenaImage } from "../types/arena";
import { debugLog } from "./debugLog";

type PositionedObject = THREE.Object3D & { userData: Record<string, unknown> };
type ChannelDataLike = { contents?: ArenaBlock[]; [key: string]: unknown };
type ApiContentsResponse = {
    data?: ArenaBlock[];
    contents?: ArenaBlock[];
    meta?: Record<string, unknown>;
};
type ElementUserData = {
    direction?: THREE.Vector3 | { x: number; y: number; z: number };
    position?: THREE.Vector3;
    positionRoot?: THREE.Vector3;
    connection?: { id?: number | string; slug?: string; _links?: { contents?: { href?: string } } };
    channelData?: { id?: number | string; slug?: string; _links?: { contents?: { href?: string } } };
    [key: string]: unknown;
};
export default class ConnectionManager {
    experience: Experience;
    scene: THREE.Scene;
    camera: THREE.Camera;
    dataFetcher: DataFetcher;
    API: FetchWrapper;
    distances: { blockConnectionDist: number; channelBlockDist: number };
    constructor(_dataFetcher: DataFetcher | null, _camera: unknown)
    {
        this.experience = new Experience()
        this.dataFetcher = this.experience.dataFetcher || new DataFetcher(null);
        this.scene = this.experience.scene
        this.camera = this.experience.camera.instance;
        this.API = this.experience.API

        this.distances = {
            blockConnectionDist : 12,
            channelBlockDist : 24
            // channelBlockDist : 6
        }
    }
    async fetchConnections(data: unknown, inputEl: PositionedObject)
    {   
        // console.log("data", data, "inputEl", inputEl);
        const connections = await this.resolveConnections(data as Partial<ArenaBlock> & { connections?: ArenaConnectionSummary[] });
        // await this.experience.sceneUtils.updateLabels(inputEl);
        if (connections && connections.length > 1) {
            linesToBillboardEls.clear()
            billboardEls.clear()
            upcomingConnections.clear()
        }
        if (connections && connections.length >= 1) {
            for(const connection of connections) {
                const channelID = connection.id;
                if (existingChannelIDs.has(channelID)) {
                    // snackBarMessage("Channel already exists");
                } else if (!existingChannelIDs.has(channelID)) {
                    const connectionLabel = new SpriteElement(connection)
                    let posFactor;
                    if(channelID === 28647202 || channelID === 2923323 || channelID === 2923335 || channelID === 2923364 || channelID === 2923359 || channelID === 2923365){
                        posFactor = 5;
                    } else {
                    if(connections.length > 50){
                    posFactor = 30;
                    } else {
                    posFactor = 10;
                    }
                    }

                    // console.log(connectionLabel, inputEl);
                    await this.positionConnection(connectionLabel.label, inputEl, posFactor)
                    // console.log(connectionLabel, inputEl);
                    const line = new LineElement(inputEl, connectionLabel.label)
                    if (connectionLabel.label) {
                        upcomingConnections.add(connectionLabel.label)
                    }
                    linesToBillboardEls.add(line.line)
                    existingChannelIDs.add(channelID);
                    // this.experience.
                }
            }
            const upcomingBox = new THREE.Box3()
            try {
                await this.experience.sceneOperations.collectUpcomingConnectionsBoundingBox(upcomingBox);
                debugLog("Collected bounding box:", upcomingBox);
    
                if (upcomingBox.isEmpty()) {
                    console.warn("Bounding box is empty");
                } else {
                    this.experience.sceneOperations.fitCameraToBoundingBox(upcomingBox);
                }
            } catch (error) {
                console.error("Error collecting or fitting bounding box:", error);
            }
            
        }
    }
    async resolveConnections(data: Partial<ArenaBlock> & { connections?: ArenaConnectionSummary[] }): Promise<ArenaConnectionSummary[]> {
        if (Array.isArray(data?.connections) && data.connections.length > 0) {
            return data.connections;
        }

        const href = getConnectionsHref(data);
        if (!href) {
            return [];
        }

        try {
            const endpoint = this.toApiEndpoint(href);
            const response = await this.API.get(endpoint);
            const resolvedConnections = normalizeConnections(response);
            data.connections = resolvedConnections;
            return resolvedConnections;
        } catch (error) {
            console.error("Error loading block connections from v3 endpoint:", error);
            return [];
        }
    }
    toApiEndpoint(href: string) {
        try {
            const url = new URL(href);
            return url.pathname.replace(/^\/v3/, "") + url.search;
        } catch {
            return href.replace(/^https?:\/\/api\.are\.na\/v3/, "");
        }
    }
    async fetchChannel(label: PositionedObject, elUserData: ElementUserData): Promise<{ data: ChannelDataLike | null; elUserData: ElementUserData; error: unknown | null } | undefined>
    {
        if (label && elUserData) {
            const connection = label.userData.connection || {};
            const channelRef = connection.slug || connection.id || label.userData.channelData?.slug || label.userData.channelData?.id;
            const contentsHref = connection?._links?.contents?.href || label.userData.channelData?._links?.contents?.href;
            const paginationQuery = "page=1&per=24";
            const contentsEndpoint = contentsHref
                ? `${this.toApiEndpoint(contentsHref)}${this.toApiEndpoint(contentsHref).includes("?") ? "&" : "?"}${paginationQuery}`
                : `/channels/${channelRef}/contents?${paginationQuery}`;
            const channelEndpoint = `/channels/${channelRef}`;
            try {
                const [channelData, contentsResponse] = await Promise.all([
                    this.API.get(channelEndpoint),
                    this.API.get(contentsEndpoint)
                ]) as [ChannelDataLike, ApiContentsResponse];
                const normalizedContents = (contentsResponse?.data || contentsResponse?.contents || []) as ArenaBlock[];
                channelData.contents = normalizedContents;
                channelData.contentsMeta = contentsResponse?.meta || null;
                label.userData.channelData = channelData;
                return { data: channelData, elUserData, error: null };
            } catch (error) {
                console.error(error);
                return { data: null, elUserData, error };
            }
        }
    }
    setBlockData(block: PositionedObject, data: ArenaBlock)
    {
        const blockClass = normalizeBlockType(data);
        if (block.type === "Sprite"){
            block.userData.blockType = "Text"
        } else {
            block.userData.blockType = blockClass;
        }
        if(blockClass === "Attachment"){
            block.userData.attachment= data.attachment;
        }
        block.userData.blockID = data.id;
        block.userData.title = data.title;
        block.userData.generatedTitle = data.generated_title;
        block.userData.content = data.content;
        block.userData.htmlContent = data.content_html;
        block.userData.description = data.description;
        block.userData.user = data.user;
        block.userData.source = data.source;
        block.userData.connections = data.connections || [];
        block.userData.creationDate = data.created_at
            ? new Date(data.created_at).toLocaleDateString()
            : "";
        block.userData.image = data.image || null;
    }
    async positionConnection(newEl: PositionedObject | null, refEl: PositionedObject | null, posFactor=6, minDistance=2)
    {   
        if(!newEl || !refEl){
            console.warn("positionConnection called without required elements", { newEl, refEl });
            return;
        }
        const cameraInstance = this.camera || this.experience.camera?.instance;
        if (cameraInstance?.quaternion) {
            await newEl.quaternion.copy(cameraInstance.quaternion);
        }
        await newEl.position.copy(refEl.position);

        // Apply initial random offset
        newEl.position.x += (Math.random() - 0.5) * posFactor / 6;
        newEl.position.y += (Math.random() - 0.5) * posFactor / 6;
        newEl.position.z += (Math.random() - 0.5) * posFactor / 6;

        if (refEl.userData?.direction) {
            newEl.position.add(refEl.userData.direction.clone().multiplyScalar(posFactor / 4));
            newEl.position.x += (Math.random() - 0.5) * posFactor / 4;
            newEl.position.y += (Math.random() - 0.5) * posFactor / 4;
            newEl.position.z += (Math.random() - 0.5) * posFactor / 4;
        }

        // Repulsion logic to avoid overlap
        this.scene.children.forEach((existingEl: THREE.Object3D) => {
            if (existingEl !== newEl && existingEl.type ==='Mesh' || existingEl !== newEl && existingEl.type ==='Sprite' || existingEl !== newEl && existingEl.name ==='pathwayItem') {
                let distance = newEl.position.distanceTo(existingEl.position);
                if (distance < minDistance) {
                    let direction = new THREE.Vector3().subVectors(newEl.position, existingEl.position).normalize();
                    let repulsionStrength = (minDistance - distance) / minDistance;
                    newEl.position.add(direction.multiplyScalar(repulsionStrength * posFactor / 2));
                }
            }
        })
        newEl.userData.position = newEl.position.clone();
    }

    async renderChannelItems(channelData: ChannelDataLike, elUserData: ElementUserData)
    {   debugLog(elUserData);
        // if a data.content of data.contents has an id equal to a plane.userData.blockID then do not render it
        billboardEls.clear()
        linesToBillboardEls.clear()
        upcomingConnections.clear()
        if(channelData && channelData.contents){
            const planeBlockIDs = this.scene.children
                .filter((object: THREE.Object3D) => object.userData && object.userData.blockID)
                .map((object: THREE.Object3D) => object.userData.blockID);
            const filteredContents = channelData.contents.filter(
                (content: ArenaBlock) => !planeBlockIDs.includes(content.id)
            );
            const batchSize = 4;
            let processedCount = 0;
            for (const content of filteredContents) {
                try {
                    await this.renderContentItem(content, elUserData);
                    processedCount += 1;
                    if (processedCount % batchSize === 0) {
                        await this.waitForNextFrame();
                    }
                } catch (error) {
                    console.error("Error rendering content item:", error, content);
                }
            }
        }
    }
    waitForNextFrame(): Promise<void> {
        return new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve());
        });
    }
    async renderContentItem(content: ArenaBlock, elUserData: ElementUserData)
    {   
        const contentString =
            typeof content?.content === "string"
                ? content.content
                : "";
        if (
            content?.state === "failed" ||
            (typeof contentString === "string" &&
                contentString.includes("Something went wrong trying to save"))
        ) {
            console.error("bugged block", content);
            return
        }
        const normalizedContent = content;
        const baseClass = normalizeBlockBaseType(normalizedContent);
        switch (baseClass) {
            case "Channel":
                const channelID = content.id;
                if (typeof channelID !== "number") {
                    return;
                }
                if (!existingChannelIDs.has(channelID)) {
                    const channel = new SpriteElement(normalizedContent)
                    if (!channel.label) {
                        return;
                    }
                    const originalLabelPosition = elUserData.position ?? new THREE.Vector3(0, 0, 0);
                    const originalLabel = new THREE.Object3D();
                    originalLabel.userData.direction = elUserData.direction;
                    originalLabel.position.copy(originalLabelPosition);
                    await this.positionConnection(channel.label, originalLabel, this.distances.blockConnectionDist);
                    if (!channel.label) {
                        return;
                    }
                    const line = new LineElement(originalLabel, channel.label)
                    linesToBillboardEls.add(line.line)
                    existingChannelIDs.add(channelID);
                    upcomingConnections.add(channel.label)
                }
                break;
            case "Block":
              switch (normalizeBlockType(normalizedContent)) {
                case "Image":
                case "Media":
                case "Link":
                case "Attachment":
                    try{
                        if (
                            normalizeBlockType(normalizedContent) === "Attachment" &&
                            !content?.image?.src &&
                            !content?.image?.display?.url &&
                            !(content.attachment as { image?: { src?: string } } | undefined)?.image?.src &&
                            !(content.attachment as { preview_url?: string } | undefined)?.preview_url
                        ) {
                            // Some attachments (e.g. mp4) have no image preview; skip plane creation.
                            return;
                        }
                        const newPlane = new Plane({data: normalizedContent});
                        await newPlane.create();
                        if(!newPlane.mesh){
                            return;
                        }
                        let originalLabelPosition
                        if(elUserData.positionRoot){
                            originalLabelPosition = elUserData.positionRoot;
                        } else {
                            originalLabelPosition = elUserData.position ?? new THREE.Vector3(0, 0, 0);
                        }
                        const originalLabel = new THREE.Object3D();
                        originalLabel.position.copy(originalLabelPosition);
                        if(elUserData.direction && !(elUserData.direction instanceof THREE.Vector3)){
                            // console.log(elUserData);
                            const direction = new THREE.Vector3(elUserData.direction.x, elUserData.direction.y, elUserData.direction.z);
                            originalLabel.userData.direction = direction;
                        } else if (elUserData.direction instanceof THREE.Vector3) {
                            originalLabel.userData.direction = elUserData.direction;
                        } else {
                            originalLabel.userData.direction = new THREE.Vector3(0, 0, 0);
                        }
                        await this.positionConnection(newPlane.mesh, originalLabel, this.distances.blockConnectionDist);
                        billboardEls.add(newPlane.mesh)
                        newPlane.addToScene()
                        const line = new LineElement(originalLabel, newPlane.mesh)
                        linesToBillboardEls.add(line.line)
                        upcomingConnections.add(newPlane.mesh)
                    } catch(error) {
                        console.error("Error creating new Plane:", error);
                    }
                    break;
                case "Text":
                    try {
                        const textLabel = await new SpriteElement(normalizedContent)
                        if (!textLabel.label) {
                            return;
                        }
                        const channelLabelPosition = elUserData.position ?? new THREE.Vector3(0, 0, 0);
                        const channelLabel = new THREE.Object3D();
                        if(elUserData.direction!== undefined){
                            channelLabel.userData.direction = elUserData.direction;
                        } else {
                            channelLabel.userData.direction = new THREE.Vector3(0,0,0)
                        }
                        channelLabel.position.copy(channelLabelPosition);
                        await this.positionConnection(textLabel.label, channelLabel, this.distances.blockConnectionDist);
                        if (!textLabel.label) {
                            return;
                        }
                        const line = new LineElement(channelLabel, textLabel.label)
                        linesToBillboardEls.add(line.line)
                        upcomingConnections.add(textLabel.label)
                    } catch(error) {
                        console.error("Error creating new Plane:", error);
                    }
                    break;
                default:
                    break;
              }
          }
    }
}